import type {CSSProperties, ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize, InputFieldType} from '@Components/input-field-v2/input-field.types';
import type {BaseSlideShowHandlers, BaseSlideShowItem} from '@Components/contextual-menu/components/slideshow-list-item/slideshow-list-item.types';
import {SlideShowItemType} from '@Components/contextual-menu/components/slideshow-list-item/slideshow-list-item.types';
import {MAX_SLIDESHOW_DURATION, MIN_SLIDE_DURATION} from '@PosterWhiteboard/items/slideshow-item/slideshow-slides.class';
import styles from './slideshow-list-item.module.scss';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {isMobile} from 'react-device-detect';

export interface SlideshowListItemProps extends BaseSlideShowItem, BaseSlideShowHandlers {
  element: ReactElement<any>;
}

export function SlideshowListItem({className = '', isSelected = false, ...props}: SlideshowListItemProps): ReactElement<any> {
  const {attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition} = useSortable({id: props.id});
  const style: CSSProperties = {
    opacity: isDragging ? 0 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const getInputSectionForSlide = (): ReactElement<any> => {
    return props.element;
  };

  const getDraggableIcon = (): ReactElement<any> => {
    if (isMobile) {
      return (
        <div className={styles.touchAction} ref={setActivatorNodeRef} {...attributes} {...listeners}>
          <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={styles.dragIcon} />
        </div>
      );
    }
    return <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={styles.dragIcon} />;
  };

  const getContentForListItem = (): ReactElement<any> => {
    return (
      <>
        {getDraggableIcon()}
        <div className={`${styles.slideNumberContainer} ${isSelected ? styles.selectedSlideNumberContainer : ''} flex-center radius-4`}>
          <Text val={(props.index + 1).toString()} className={`${isSelected ? 'content-body-white' : ''}`} size={TextSize.XSMALL} bold />
        </div>
        <div className={`flexbox flex-1 ${styles.inputsContainer} ${styles.itemsGap}`}>
          <div className={`${styles.mainSlideContainerAnimation} ${props.duration?.showDuration ? styles.slideInput : styles.fullWidthInput}`}>{getInputSectionForSlide()}</div>
          {props.duration?.showDuration ? (
            <div className={styles.durationInput}>
              <InputField
                className={props.type === SlideShowItemType.TEXT ? '_full-height' : ''}
                placeholder=" "
                inputType={InputFieldType.NUMBER}
                onInputChange={(val: number): void => {
                  props.duration?.onChangeDuration(val, props.id);
                }}
                onInputChangeEnded={(val: number): void => {
                  props.duration?.onChangeDurationEnded(val, props.id);
                }}
                inputClassName={styles.durationInputClassName}
                iconClassName="spacing-p-l-1"
                input={props.duration.duration}
                size={InputFieldSize.SMALL}
                hasIcon="icon-clock-o"
                allowOnChangeWithKeys
                min={MIN_SLIDE_DURATION}
                max={MAX_SLIDESHOW_DURATION}
                selectOnFocus
                typingStoppedTimeout={1500}
              />
            </div>
          ) : null}
        </div>
        <Icon
          icon="icon-delete"
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          className={`${styles.deleteIcon} ${isSelected ? styles.selectedDeleteIcon : ''}`}
          onClick={(e): void => {
            e.stopPropagation();
            props.onDelete(props.id);
          }}
        />
      </>
    );
  };

  if (isMobile) {
    return (
      <div
        ref={setNodeRef}
        style={{...style}}
        id={props.id}
        onClick={(): void => {
          props.onClick(props.id, props.index);
        }}
        className={`${styles.container} ${styles.itemsGap} ${className} flex-h-row flex-items-center ${isSelected ? styles.selectedContainer : ''} radius-4`}
      >
        {getContentForListItem()}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{...style}}
      id={props.id}
      onClick={(): void => {
        props.onClick(props.id, props.index);
      }}
      className={`${styles.container} ${styles.touchAction} ${styles.itemsGap} ${className} flex-h-row flex-items-center ${isSelected ? styles.selectedContainer : ''} radius-4`}
    >
      {getContentForListItem()}
    </div>
  );
}
